// ManageUsers.js
import React, { useState, useEffect } from 'react';

const ManageUsers = ({ backendUrl, resetKey }) => {
  const [users, setUsers] = useState([]);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    isVerified: false,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [originalUserData, setOriginalUserData] = useState(null);
  const [userPasswords, setUserPasswords] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [nameFilter, setNameFilter] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Fetch users and reset state when resetKey changes
  useEffect(() => {
    fetchUsers();
    fetchAssignable();
    // Reset internal state
    setSelectedUser(null);
    setSelectedRoleIds([]);
    setSelectedGroupIds([]);
    setShowCreateForm(false);
    setNewUserData({
      name: '',
      email: '',
      password: '',
      isVerified: false,
    });
    setUserPasswords({});
    // eslint-disable-next-line
  }, [resetKey]);

  // Fetch roles + groups available for assignment (needs only users:manage)
  const fetchAssignable = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/assignable-options`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setAvailableRoles(result.roles || []);
        setAvailableGroups(result.groups || []);
      }
    } catch (error) {
      console.error('Error fetching assignable options:', error);
    }
  };

  // Effective permission keys = union of selected roles' + selected groups' permissions
  const effectivePermissions = React.useMemo(() => {
    const set = new Set();
    availableRoles
      .filter((r) => selectedRoleIds.includes(r._id))
      .forEach((r) => (r.permissions || []).forEach((p) => set.add(p)));
    availableGroups
      .filter((g) => selectedGroupIds.includes(g._id))
      .forEach((g) => (g.permissions || []).forEach((p) => set.add(p)));
    return Array.from(set).sort();
  }, [availableRoles, availableGroups, selectedRoleIds, selectedGroupIds]);

  // Fetch all users (admin functionality)
  const fetchUsers = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/getusers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setUsers(result.users);
      } else {
        console.error('Error fetching users:', result.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserData({ ...newUserData, [name]: value });
  };

  const createNewUser = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/createuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify(newUserData),
      });
      const result = await response.json();
      if (result.success) {
        alert('New user created successfully!');
        setNewUserData({ name: '', email: '', password: '', isVerified: false });
        setShowCreateForm(false);
        fetchUsers();
      } else {
        alert(result.message || 'Error creating new user.');
      }
    } catch (error) {
      console.error('Error creating new user:', error);
      alert('An error occurred while creating a new user.');
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setOriginalUserData({ ...user }); // Store original data to compare changes
    // Preload assigned roles + groups (both populated: [{ _id, name, displayName }])
    setSelectedRoleIds((user.roles || []).map((r) => (typeof r === 'object' ? r._id : r)));
    setSelectedGroupIds((user.groups || []).map((g) => (typeof g === 'object' ? g._id : g)));
  };

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const saveUserRoles = async () => {
    if (!selectedUser) return;
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/assignroles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({
          userId: selectedUser._id,
          roleIds: selectedRoleIds,
          groupIds: selectedGroupIds,
        }),
      });
      const result = await response.json();
      if (result.success) {
        alert('Roles & groups updated successfully!');
        fetchUsers();
      } else {
        alert(result.message || 'Error updating roles.');
      }
    } catch (error) {
      console.error('Error updating roles:', error);
      alert('An error occurred while updating roles.');
    }
  };

  const handleUserPasswordChange = (e, userId) => {
    const { value } = e.target;
    setUserPasswords({
      ...userPasswords,
      [userId]: value,
    });
  };

  const updateProfile = async () => {
    if (!selectedUser) return;
    const token = localStorage.getItem('auth-token');

    // Only include fields that have changed
    const dataToUpdate = {};
    if (selectedUser.name !== originalUserData.name) {
      dataToUpdate.name = selectedUser.name;
    }
    if (selectedUser.email !== originalUserData.email) {
      dataToUpdate.email = selectedUser.email;
    }
    if (selectedUser.isVerified !== originalUserData.isVerified) {
      dataToUpdate.isVerified = selectedUser.isVerified;
    }
    if (selectedUser.allowMultipleSessions !== originalUserData.allowMultipleSessions) {
      dataToUpdate.allowMultipleSessions = selectedUser.allowMultipleSessions;
    }
    dataToUpdate.userId = selectedUser._id;

    if (Object.keys(dataToUpdate).length <= 1) { // Only userId present, no changes made
      alert('No changes detected.');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/user/updateuserdata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify(dataToUpdate),
      });

      const result = await response.json();
      if (result.success) {
        alert('Profile updated successfully!');
        setSelectedUser(null);
        setOriginalUserData(null);
        fetchUsers();
      } else {
        alert(result.message || 'Error updating profile.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An error occurred while updating the profile.');
    }
  };

  const changeUserPassword = async (userId, newPassword) => {
    if (!newPassword) return;

    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/changepassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ userId, newPassword }),
      });
      
      const result = await response.json();
      if (result.success) {
        alert('Password updated successfully!');
        setUserPasswords({
          ...userPasswords,
          [userId]: '',
        });
      } else {
        alert(result.message || 'Error updating password.');
      }
    } catch (error) {
      console.error('Error updating user password:', error);
      alert('An error occurred while updating the password.');
    }
  };

  const deleteUser = async (userId) => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/user/deleteuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ userId }),
      });
      const result = await response.json();
      if (result.success) {
        alert('User deleted successfully!');
        fetchUsers();
        setSelectedUser(null);
      } else {
        alert(result.message || 'Error deleting user.');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('An error occurred while deleting the user.');
    }
  };

  const handleUpdateUserData = async () => {
    if (selectedUser) {
      await updateProfile();
      if (userPasswords[selectedUser._id]) {
        await changeUserPassword(selectedUser._id, userPasswords[selectedUser._id]);
      }
    }
  };

  const sortUsers = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredUsers = React.useMemo(() => {
    let filtered = [...users];
    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      filtered = filtered.filter((user) =>
        user.name.toLowerCase().includes(q) ||
        (user.email || '').toLowerCase().includes(q)
      );
    }
    if (groupFilter) {
      filtered = filtered.filter((user) =>
        (user.groups || []).some((g) => (typeof g === 'object' ? g._id : g) === groupFilter)
      );
    }
    return filtered;
  }, [users, nameFilter, groupFilter]);

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  return (
    <div className="form-group">
      <h2>Manage Users</h2>

      {selectedUser ? (
        <div className="form-group">
          <h3>Update User: {selectedUser.name}</h3>
          <label htmlFor="name">Name</label>
          <input
            type="text"
            value={selectedUser.name}
            onChange={(e) =>
              setSelectedUser({ ...selectedUser, name: e.target.value })
            }
          />
          <label htmlFor="email">Email</label>
          <input
            type="email"
            value={selectedUser.email}
            onChange={(e) =>
              setSelectedUser({ ...selectedUser, email: e.target.value })
            }
          />
          <label htmlFor="isVerified">
            <input
              type="checkbox"
              id="isVerified"
              checked={selectedUser.isVerified || false}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, isVerified: e.target.checked })
              }
              style={{ width: 'auto', marginRight: '8px' }}
            />
            Verified (account active — can log in without email verification)
          </label>
          <label htmlFor="allowMultipleSessions">
            <input
              type="checkbox"
              id="allowMultipleSessions"
              checked={selectedUser.allowMultipleSessions || false}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, allowMultipleSessions: e.target.checked })
              }
              style={{ width: 'auto', marginRight: '8px' }}
            />
            Allow Multiple Sessions (user can login from multiple devices simultaneously)
          </label>
          <label htmlFor="password">Change Password</label>
          <input
            type="password"
            placeholder="New Password"
            value={userPasswords[selectedUser._id] || ''}
            onChange={(e) => handleUserPasswordChange(e, selectedUser._id)}
          />
          <button onClick={handleUpdateUserData}>Update User Data</button>
          <button style={{ backgroundColor: 'red' }} onClick={() => deleteUser(selectedUser._id)}>Delete User</button>
          <button onClick={() => setSelectedUser(null)}>Back to List</button>

          <h3 style={{ marginTop: '24px' }}>Roles (Dashboard access)</h3>
          <p style={{ color: '#a0aec0', fontSize: '13px', marginTop: 0 }}>
            Assign roles to control which Dashboard sections this user can access.
          </p>
          {availableRoles.length === 0 ? (
            <p style={{ color: '#a0aec0' }}>No roles available. Create roles in "Manage Roles".</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
              {availableRoles.map((role) => (
                <label key={role._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role._id)}
                    onChange={() => toggleRole(role._id)}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  {role.displayName}
                  <span style={{ color: '#718096', fontSize: '12px' }}>({role.name})</span>
                </label>
              ))}
            </div>
          )}

          <h3 style={{ marginTop: '20px' }}>Groups</h3>
          <p style={{ color: '#a0aec0', fontSize: '13px', marginTop: 0 }}>
            Members inherit every role of the groups they belong to.
          </p>
          {availableGroups.length === 0 ? (
            <p style={{ color: '#a0aec0' }}>No groups available. Create groups in "Manage Groups".</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
              {availableGroups.map((group) => (
                <label key={group._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group._id)}
                    onChange={() => toggleGroup(group._id)}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  {group.displayName}
                  <span style={{ color: '#718096', fontSize: '12px' }}>({group.name})</span>
                </label>
              ))}
            </div>
          )}

          <div style={{ margin: '16px 0', padding: '10px 14px', background: '#1a202c', borderRadius: '8px' }}>
            <strong style={{ fontSize: '13px' }}>Effective permissions (roles + groups):</strong>
            <div style={{ marginTop: '6px' }}>
              {effectivePermissions.length > 0 ? (
                effectivePermissions.map((p) => (
                  <span key={p} style={{
                    display: 'inline-block', margin: '3px', padding: '3px 10px',
                    borderRadius: '12px', fontSize: '12px', background: '#2d3748', color: '#e2e8f0'
                  }}>{p}</span>
                ))
              ) : (
                <span style={{ color: '#a0aec0' }}>No permissions (no Dashboard access).</span>
              )}
            </div>
          </div>

          <button onClick={saveUserRoles}>Save Roles & Groups</button>
        </div>
      ) : showCreateForm ? (
        <div className="form-group">
          <h3>Create New User</h3>
          <input
            type="text"
            name="name"
            placeholder="Name"
            value={newUserData.name}
            onChange={handleNewUserChange}
          />
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={newUserData.email}
            onChange={handleNewUserChange}
          />
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={newUserData.password}
            onChange={handleNewUserChange}
          />
          <label htmlFor="newIsVerified">
            <input
              type="checkbox"
              id="newIsVerified"
              checked={newUserData.isVerified}
              onChange={(e) =>
                setNewUserData({ ...newUserData, isVerified: e.target.checked })
              }
              style={{ width: 'auto', marginRight: '8px' }}
            />
            Mark as verified (account active immediately, skips email verification)
          </label>
          <p style={{ color: '#a0aec0', fontSize: '13px', marginTop: 0 }}>
            After creating, assign roles by selecting the user in the list.
          </p>
          <button onClick={createNewUser}>Create User</button>
          <button onClick={() => setShowCreateForm(false)}>Back to List</button>
        </div>
      ) : (
        <>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ marginBottom: '12px' }}
          >
            + Create New User
          </button>
          <div className="filters">
            <label>
              Search:
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search by name or email..."
              />
            </label>
            <label>
              Filter by Group:
              <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                <option value="">All</option>
                {availableGroups.map((g) => (
                  <option key={g._id} value={g._id}>{g.displayName}</option>
                ))}
              </select>
            </label>
          </div>
          <table>
            <thead>
              <tr>
                <th onClick={() => sortUsers('name')}>Name</th>
                <th onClick={() => sortUsers('email')}>Email</th>
                <th style={{ textAlign: 'center' }}>Roles</th>
                <th style={{ textAlign: 'center' }}>Groups</th>
                <th style={{ textAlign: 'center' }}>Verified</th>
                <th style={{ textAlign: 'center' }}>Multiple Sessions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr
                  key={user._id}
                  onClick={() => handleSelectUser(user)}
                >
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td style={{ textAlign: 'center' }}>
                    {user.roles && user.roles.length > 0
                      ? user.roles.map((r) => (typeof r === 'object' ? r.displayName : r)).join(', ')
                      : <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {user.groups && user.groups.length > 0
                      ? user.groups.map((g) => (typeof g === 'object' ? g.displayName : g)).join(', ')
                      : <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {user.isVerified ? (
                      <span style={{ color: '#48bb78', fontWeight: 'bold' }}>✓ Verified</span>
                    ) : (
                      <span style={{ color: '#f56565' }}>✗ Pending</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {user.allowMultipleSessions ? (
                      <span style={{ color: '#48bb78', fontWeight: 'bold' }}>✓ Enabled</span>
                    ) : (
                      <span style={{ color: '#a0aec0' }}>Single Session</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default ManageUsers;
